import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ChatbotService } from "../chatbot.service";

@Component({
  selector: "app-chatbot-workspace",
  templateUrl: "./chatbot-workspace.component.html",
  styleUrls: ["./chatbot-workspace.component.css"],
})
export class ChatbotWorkspaceComponent implements OnInit, OnDestroy {
  isLoaded = false;
  isLoadingToken = false;
  isLoadingHistory = false;
  hasError = false;
  historyAllLoaded = false;

  chatbotSrc: SafeResourceUrl | null = null;
  historyItems: any[] = [];
  historyPage = 1;
  historySize = 10;
  historyTotal = 0;
  currentConversationId: string | null = null;
  silentHistoryRefresh = false;
  private pendingHistoryRefresh = false;

  private rawChatbotUrl: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private chatbotService: ChatbotService,
  ) {}

  ngOnInit() {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.currentConversationId = params.get("conversationId");
      if (this.rawChatbotUrl) {
        this.loadConversation(this.currentConversationId);
      }
    });

    this.loadChatbot();
    this.resetHistory();
    this.loadHistory();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack() {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        return;
      }
    } catch (error) {
      console.warn("Unable to return to opener tab:", error);
    }

    window.history.back();
  }

  restartChat() {
    this.currentConversationId = null;
    this.isLoaded = false;
    this.hasError = false;
    this.chatbotSrc = null;

    setTimeout(() => {
      if (this.rawChatbotUrl) {
        this.loadConversation(null);
        this.refreshHistory(true);
      } else {
        this.loadChatbot();
      }
    }, 100);
  }

  startNewChat() {
    this.currentConversationId = null;

    if (this.rawChatbotUrl) {
      this.loadConversation(null);
      this.refreshHistory(true);
    } else {
      this.loadChatbot();
    }
  }

  retryLoad() {
    this.hasError = false;
    this.loadChatbot();
  }

  selectHistory(item: any) {
    if (!item || !item.id) {
      return;
    }

    this.currentConversationId = item.id;
    this.loadConversation(item.id);
    this.refreshHistory(true);
  }

  onHistoryScroll(event: any) {
    const element = event.target;
    const threshold = 80;
    if (element.scrollHeight - element.scrollTop - element.clientHeight <= threshold) {
      if (!this.isLoadingHistory && !this.historyAllLoaded) {
        this.loadHistory();
      }
    }
  }

  trackByHistory(index: number, item: any) {
    return item && item.id ? item.id : index;
  }

  private loadChatbot() {
    this.hasError = false;
    this.isLoadingToken = true;
    this.isLoaded = false;

    this.chatbotService
      .getChatbotUrl()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chatbotUrl) => {
          this.rawChatbotUrl = chatbotUrl;
          this.loadConversation(this.currentConversationId);
          this.isLoadingToken = false;
          this.refreshHistory();
        },
        error: (error) => {
          console.error("Failed to load chatbot workspace:", error);
          this.hasError = true;
          this.isLoadingToken = false;
        },
      });
  }

  private loadConversation(conversationId: string | null) {
    if (!this.rawChatbotUrl) {
      return;
    }

    this.isLoaded = false;
    this.chatbotSrc = null;

    const chatUrl = conversationId
      ? this.chatbotService.appendConversationId(this.rawChatbotUrl, conversationId)
      : this.rawChatbotUrl;

    setTimeout(() => {
      this.chatbotSrc = this.sanitizer.bypassSecurityTrustResourceUrl(chatUrl);
      this.isLoaded = true;
    }, 50);
  }

  private resetHistory() {
    this.historyItems = [];
    this.historyPage = 1;
    this.historyTotal = 0;
    this.historyAllLoaded = false;
  }

  private refreshHistory(silent: boolean = false) {
    if (this.isLoadingHistory) {
      this.pendingHistoryRefresh = true;
      this.silentHistoryRefresh = this.silentHistoryRefresh || silent;
      return;
    }

    this.pendingHistoryRefresh = false;
    this.silentHistoryRefresh = silent;
    if (!silent) {
      this.resetHistory();
    } else {
      this.historyPage = 1;
      this.historyAllLoaded = false;
    }
    this.loadHistory();
  }

  private loadHistory() {
    if (this.isLoadingHistory || this.historyAllLoaded) {
      return;
    }

    this.isLoadingHistory = true;
    this.chatbotService
      .getHistory(this.historyPage, this.historySize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.items) {
            if (this.silentHistoryRefresh) {
              this.historyItems = res.items;
            } else {
              this.historyItems = this.historyItems.concat(res.items);
            }
            
            this.historyTotal = res.total || 0;
            if (this.historyPage >= res.pages || res.items.length === 0) {
              this.historyAllLoaded = true;
            } else {
              this.historyPage++;
            }
          } else {
            this.historyAllLoaded = true;
          }
          this.isLoadingHistory = false;
          this.silentHistoryRefresh = false;
          if (this.pendingHistoryRefresh) {
            this.refreshHistory();
          }
        },
        error: (error) => {
          console.error("Failed to fetch chatbot workspace history:", error);
          this.isLoadingHistory = false;
          this.silentHistoryRefresh = false;
          if (this.pendingHistoryRefresh) {
            this.refreshHistory();
          }
        },
      });
  }

}
